import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, ElementRef, HostBinding, HostListener, OnInit, inject, input } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { filter } from "rxjs/operators";
import { IArea, IPoint } from "../../models";
import {
  ActivityIndicatorService,
  ArPointerActivatedEvent,
  ArPointerDeactivatedEvent,
  debug,
  PointerService,
  StreamState,
} from "../../services";
import { TileComponent } from "../tile/tile.component";

@Component({
  selector: "app-point-area",
  templateUrl: "./point-area.component.html",
  styleUrls: ["./point-area.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PointAreaComponent implements OnInit {
  private pointerService = inject(PointerService);
  private cdr = inject(ChangeDetectorRef);
  private activityService = inject(ActivityIndicatorService);
  private destroyRef = inject(DestroyRef);
  host = inject<ElementRef<HTMLDivElement>>(ElementRef);
  private parent = inject(TileComponent, { optional: true });

  readonly stream = input<StreamState>(undefined);

  points: IPoint[] = [];

  ngOnInit(): void {
    this.pointerService.pointAvailable$
      .pipe(
        filter(
          (p) =>
            p.endpoint === this.target &&
            p.streamCorrelationId === this.stream().correlationId
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((point) => {
        debug("point-area:point available:", point);
        this.points = [...this.points, point];
        this.cdr.detectChanges();
      });

    this.pointerService.pointAreaChange$
      .pipe(
        filter(
          (d) =>
            d.targetUserEndpointId === this.target &&
            d.targetStreamCorrelationId === this.streamCorrelationId
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((data) => {
        debug("point-area:point area change:", data);
        switch (data.type) {
          case ArPointerDeactivatedEvent.type:
            this.points = [];
            this.parent.ArPointerOn = false;
            this.activityService.showIconMessage(
              "laser",
              "Laser pointer deactivated",
              1
            );
            break;
          case ArPointerActivatedEvent.type:
            this.parent.ArPointerOn = true;
            this.activityService.showIconMessage(
              "laser",
              "Laser pointer activated",
              1
            );
            break;
        }
        this.cdr.detectChanges();
      });
  }

  @HostBinding("class") get class() {
    return {
      "point-area-active": this.active,
      "point-area-sender": this.isSender,
    };
  }

  @HostListener("click", ["$event"])
  onClick(e) {
    if (!this.parent?.isWhisper) {
      this.point(e);
    }
  }

  trackByFn(index: number, el: IPoint) {
    return el.id;
  }

  point(e: MouseEvent) {
    if (this.active && this.isSender) {
      if (
        // @ts-expect-error
        e.target.classList &&
        // @ts-expect-error
        e.target.classList.contains("point-area-active")
      ) {
        this.pointerService.point(
          e,
          this.target,
          this.type,
          this.stream().correlationId
        );
      }
    }
  }

  pointerDestroyed(point: IPoint) {
    this.points = this.points.filter((p) => p?.id !== point.id);
  }

  get streamCorrelationId() {
    return this.stream()?.correlationId;
  }

  get area(): IArea {
    return {
      width: this.parent?.container.clientWidth,
      height: this.parent?.container.clientHeight,
    };
  }

  get target() {
    return this.stream().originator.endpoint;
  }

  get type() {
    return this.stream().type;
  }

  get active() {
    return this.pointerService.isTarget(
      this.target,
      this.stream().type,
      this.stream().correlationId
    );
  }
  get isSender() {
    return !this.parent?.isWhisper; // this.pointerService.isRequester(this.myself.endpoint);
  }
}
